import { Link, router } from "@inertiajs/react";
import type Producer from "@/types/serializers/Producer";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";

type Props = {
  producer: Producer;
  flash?: { notice?: string; alert?: string };
};

export default function ProducersShow({ producer, flash }: Props) {
  useFlashToast(flash);

  function handleDelete() {
    if (!confirm("Are you sure you want to delete this producer?")) return;
    router.delete(`/producers/${producer.id}`);
  }

  return (
    <div className="py-8 px-4">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/producers">
            <ArrowLeft className="size-4" />
            Back to producers
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>{producer.name}</CardTitle>
            <CardDescription>Producer details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Name</p>
              <p className="text-sm">{producer.name || <span className="italic text-muted-foreground">—</span>}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm">{producer.description || <span className="italic text-muted-foreground">No description</span>}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">City</p>
                <p className="text-sm">{producer.city || <span className="italic text-muted-foreground">—</span>}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">State</p>
                <p className="text-sm">{producer.state || <span className="italic text-muted-foreground">—</span>}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Postal Code</p>
                <p className="text-sm">{producer.postalCode || <span className="italic text-muted-foreground">—</span>}</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Address</p>
              <p className="text-sm">
                {producer.addressLine1 || producer.addressLine2 ? (
                  <>
                    {producer.addressLine1 && <span>{producer.addressLine1}<br /></span>}
                    {producer.addressLine2 && <span>{producer.addressLine2}</span>}
                  </>
                ) : (
                  <span className="italic text-muted-foreground">—</span>
                )}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Country</p>
              <p className="text-sm">{producer.country?.name || <span className="italic text-muted-foreground">—</span>}</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" asChild>
                <Link href={`/producers/${producer.id}/edit`}>
                  <Pencil className="size-4" />
                  Edit
                </Link>
              </Button>
              <Button size="sm" variant="destructive" onClick={handleDelete}>
                <Trash2 className="size-4" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
